import { useState, useEffect } from 'react';
import { CheckCircle, MapPin, ShoppingBag, Wrench, Star, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Random first names for realistic notifications
const firstNames = [
  'James', 'John', 'Robert', 'Michael', 'David', 'William', 'Richard', 'Joseph',
  'Thomas', 'Christopher', 'Sarah', 'Jennifer', 'Lisa', 'Amanda', 'Emily', 'Jessica',
  'Ashley', 'Melissa', 'Michelle', 'Stephanie', 'Maria', 'Jose', 'Carlos', 'Luis'
];

// Fort Wayne area locations
const locations = [
  'Fort Wayne', 'New Haven', 'Leo-Cedarville', 'Huntertown', 'Grabill',
  'Woodburn', 'Monroeville', 'Decatur', 'Auburn', 'Bluffton',
  'Columbia City', 'Kendallville', 'Angola', 'Warsaw', 'Huntington'
];

// Service types with icons and messages
const serviceTypes = {
  tow: { icon: Wrench, message: 'requested towing service' },
  jump_start: { icon: Wrench, message: 'got a jump start' },
  flat_tire: { icon: Wrench, message: 'got tire assistance' },
  lockout: { icon: Wrench, message: 'got lockout help' },
  repair: { icon: Wrench, message: 'booked mobile repair' },
  diagnostic: { icon: Wrench, message: 'scheduled a diagnostic' },
};

// Generate realistic time strings
const getTimeAgo = () => {
  const times = [
    '2 minutes ago',
    '5 minutes ago',
    '8 minutes ago',
    '12 minutes ago',
    '15 minutes ago',
    '23 minutes ago',
    '32 minutes ago',
    '1 hour ago',
  ];
  return times[Math.floor(Math.random() * times.length)];
};

// Generate a random notification
const generateNotification = () => {
  const name = firstNames[Math.floor(Math.random() * firstNames.length)];
  const location = locations[Math.floor(Math.random() * locations.length)];
  const serviceKeys = Object.keys(serviceTypes);
  const serviceKey = serviceKeys[Math.floor(Math.random() * serviceKeys.length)];
  const service = serviceTypes[serviceKey];
  
  // Random notification types
  const types = ['service', 'purchase', 'review'];
  const type = types[Math.floor(Math.random() * types.length)];
  
  if (type === 'service') {
    return {
      id: Date.now(),
      type: 'service',
      name,
      location,
      message: service.message,
      icon: service.icon,
      time: getTimeAgo(),
    };
  } else if (type === 'purchase') {
    return {
      id: Date.now(),
      type: 'purchase',
      name,
      location,
      message: 'purchased from marketplace',
      icon: ShoppingBag,
      time: getTimeAgo(),
    };
  } else {
    return {
      id: Date.now(),
      type: 'review',
      name,
      location,
      message: 'left a 5-star review',
      icon: Star,
      time: getTimeAgo(),
    };
  }
};

// Single notification popup component
function NotificationPopup({ notification, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const IconComponent = notification.icon;

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 100);
    
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      setIsLeaving(true);
      setTimeout(onClose, 300);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(onClose, 300);
  };

  return (
    <div 
      className={`
        fixed bottom-4 left-4 z-50 max-w-sm
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}
      `}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        {/* Accent bar */}
        <div className="h-1 bg-gradient-to-r from-green-500 to-emerald-500" />
        
        <div className="p-4 flex items-start gap-3">
          {/* Icon */}
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
            ${notification.type === 'service' ? 'bg-blue-100 text-blue-600' : ''}
            ${notification.type === 'purchase' ? 'bg-purple-100 text-purple-600' : ''}
            ${notification.type === 'review' ? 'bg-yellow-100 text-yellow-600' : ''}
          `}>
            <IconComponent className="w-5 h-5" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900">
              <span className="font-semibold">{notification.name}</span>
              {' from '}
              <span className="font-medium text-gray-700">{notification.location}</span>
              {' '}
              <span className="text-gray-600">{notification.message}</span>
            </p>
            <div className="flex items-center gap-2 mt-1">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span className="text-xs text-gray-500">{notification.time}</span>
            </div>
          </div>
          
          {/* Close button */}
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Verified badge */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle className="w-3 h-3" />
            <span>Verified by Jiffys Mobile</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main FOMO notifications manager
export function FomoNotifications({ 
  enabled = true, 
  interval = 45000, // Show notification every 45 seconds
  initialDelay = 5000 // Wait 5 seconds before first notification
}) {
  const [currentNotification, setCurrentNotification] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!enabled || !mounted) return;

    // Check if user has dismissed notifications this session
    const dismissed = sessionStorage.getItem('fomo_dismissed');
    if (dismissed) return;

    let timeoutId;
    let intervalId;

    // Show first notification after initial delay
    timeoutId = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') console.log('FOMO: Showing first notification');
      setCurrentNotification(generateNotification());
      
      // Then show notifications at regular intervals
      intervalId = setInterval(() => {
        if (!document.hidden) {
          if (process.env.NODE_ENV === 'development') console.log('FOMO: Showing interval notification');
          setCurrentNotification(generateNotification());
        }
      }, interval);
    }, initialDelay);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [enabled, interval, initialDelay, mounted]);

  const handleClose = () => {
    setCurrentNotification(null);
  };

  if (!currentNotification) return null;

  return (
    <NotificationPopup 
      notification={currentNotification} 
      onClose={handleClose}
    />
  );
}

// Compact notification for mobile
export function MobileFomoNotifications({ enabled = true }) {
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const showNotification = () => {
      const notif = generateNotification();
      setNotification(notif);
      setIsVisible(true);
      
      // Hide after 4 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
    };

    // First notification after 10 seconds
    const firstTimeout = setTimeout(showNotification, 10000);
    
    // Then every 60 seconds
    const interval = setInterval(showNotification, 60000);

    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, [enabled]);

  if (!notification) return null;

  return (
    <div 
      className={`
        fixed bottom-20 left-2 right-2 z-40 sm:hidden
        transform transition-all duration-300
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'}
      `}
    >
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 flex items-center gap-3">
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-4 h-4 text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-900 truncate">
            <span className="font-semibold">{notification.name}</span>
            {' from '}
            {notification.location}
            {' '}
            {notification.message}
          </p>
          <p className="text-xs text-gray-500">{notification.time}</p>
        </div>
      </div>
    </div>
  );
}

// Real activity from backend (for landing page)
export function RealActivityNotifications({ enabled = true }) {
  const [activities, setActivities] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const fetchActivities = async () => {
      try {
        const response = await axios.get(`${API}/platform/recent-activity`);
        if (response.data.activities && response.data.activities.length > 0) {
          setActivities(response.data.activities);
        }
      } catch (error) {
        // Fall back to generated notifications
        const generated = Array(5).fill(null).map(() => generateNotification());
        setActivities(generated);
      }
    };

    fetchActivities();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || activities.length === 0) return;

    // Show notification every 30 seconds
    const interval = setInterval(() => {
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
      setCurrentIndex((prev) => (prev + 1) % activities.length);
    }, 30000);

    // Show first one after 5 seconds
    const firstTimeout = setTimeout(() => {
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(firstTimeout);
    };
  }, [enabled, activities]);

  if (!isVisible || activities.length === 0) return null;

  const activity = activities[currentIndex];
  
  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm animate-slide-in-left">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-900">{activity.message || activity.name}</p>
            <p className="text-xs text-gray-500">{activity.time}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FomoNotifications;
